import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import AdminTable from '../components/AdminTable';
import AdminModal from '../components/AdminModal';
import styles from './UserManagement.module.css';
import { PlusIcon } from '../../components/Icons';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    is_admin: false
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/');
      setUsers(response.data);
    } catch (error) {
      console.error('获取用户列表失败:', error);
      alert('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      password: '',
      is_admin: false
    });
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '', // 编辑时不显示密码
      is_admin: user.is_admin || false
    });
    setShowModal(true);
  };

  const handleDelete = async (user) => {
    if (window.confirm(`确定要删除用户 "${user.username}" 吗？`)) {
      try {
        await api.delete(`/users/${user.id}`);
        fetchUsers();
        alert('用户删除成功');
      } catch (error) {
        console.error('删除用户失败:', error);
        alert('删除用户失败');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        // 编辑用户
        const updateData = {
          username: formData.username,
          is_admin: formData.is_admin
        };
        // 只有当密码不为空时才更新密码
        if (formData.password.trim()) {
          updateData.password = formData.password;
        }
        
        await api.put(`/users/${editingUser.id}`, updateData);
        alert('用户更新成功');
      } else {
        // 创建新用户
        await api.post('/users/', formData);
        alert('用户创建成功');
      }
      
      setShowModal(false);
      fetchUsers();
    } catch (error) {
      console.error('保存用户失败:', error);
      alert('保存用户失败');
    }
  };

  const columns = [
    { key: 'id', label: 'ID', width: '80px' },
    { key: 'username', label: '用户名' },
    { 
      key: 'is_admin', 
      label: '管理员', 
      render: (value) => (
        <span className={`${styles.badge} ${value ? styles.admin : styles.user}`}>
          {value ? '管理员' : '普通用户'}
        </span>
      )
    },
    { key: 'created_at', label: '创建时间', render: (value) => new Date(value).toLocaleDateString() },
    { key: 'updated_at', label: '更新时间', render: (value) => new Date(value).toLocaleDateString() }
  ];

  return (
    <div className={styles.userManagement}>
      <div className={styles.header}>
        <h1 className={styles.title}>用户管理</h1>
        <button className={styles.createBtn} onClick={handleCreate}>
          <PlusIcon size={18} />
          添加用户
        </button>
      </div>

      <AdminTable
        data={users}
        columns={columns}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        emptyMessage="暂无用户数据"
      />

      {showModal && (
        <AdminModal
          title={editingUser ? '编辑用户' : '添加用户'}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
        >
          <div className={styles.form}>
            <div className={styles.formGroup}>
              <label className={styles.label}>用户名</label>
              <input
                type="text"
                className={styles.input}
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                密码 {editingUser && <span className={styles.hint}>(留空则不修改)</span>}
              </label>
              <input
                type="password"
                className={styles.input}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editingUser}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.is_admin}
                  onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                />
                <span className={styles.checkboxText}>管理员权限</span>
              </label>
            </div>
          </div>
        </AdminModal>
      )}
    </div>
  );
}

export default UserManagement;
